import React, {
    useEffect, useMemo,
} from 'react';
import {
    isAnyOfThreeBProductTypes,
} from 'hooks/isThreeBProduct';
import useCommonAllocationDataProvider
    from 'pages/ClientOverview/pages/Portfolios/context/useCommonAllocationDataProvider';
import { INVESTOR_PROFILE_CONTACT_ID, PRODUCTS } from 'constants/constants';
import Preloader from 'components/Preloader';
import { getModel } from 'datasource/usePersonalDetails/models/getModel';
import { useFormatting } from 'locale';
import { isEmpty } from 'lodash';
import { useProductsList } from 'hooks/useProducts';
import { useGetCustomService } from 'hooks/rest/useGetCustomService';
import {
    useAdvisoryDocument,
} from '../hooks/useAdvisoryDocument';
import { usePersonalDetails } from '../hooks/usePersonalDetails';
import ChangeStrategyContext from './context';
import { useRiskProfile } from '../hooks/useRiskProfile';
import { useStrategy } from '../hooks/useStrategy';
import { usePortfolio } from '../../../../../../../domain/Portfolio';
import { useModelList } from '../../../hooks/useModelList';

type ChangeStrategyProviderProps = {
    clientId: number;
    portfolioId: number | string;
    productKey?: string;
    children: React.ReactNode;
};

const ChangeStrategyProvider = ({
    clientId,
    portfolioId,
    productKey = undefined,
    children,
}: ChangeStrategyProviderProps) => {
    const { getFormattedDate } = useFormatting();
    const { portfolio: { dataRaw: portfolio, isLoading } } = usePortfolio() as any;

    const {
        products,
    } = useProductsList(undefined, !!productKey) as any;

    const newProduct = productKey ? products?.find((prod: any) => prod?.settings?.productKey?.toLowerCase() === productKey?.toLowerCase()) : null;

    const newProductExternalId = useMemo(() => {
        if (productKey && newProduct?.externalId && portfolio?.Product?.Id !== newProduct?.id) {
            return newProduct?.externalId;
        }

        return undefined;
    }, [productKey, newProduct?.externalId, portfolio?.Product?.Id]);

    const productId = useMemo(() => {
        if (productKey) {
            return newProduct?.id || undefined;
        }

        return portfolio?.Product?.Id;
    }, [productKey, newProduct?.id, portfolio?.Product?.Id]);

    const personalDetails = usePersonalDetails();
    const advisoryDocument = useAdvisoryDocument();
    const riskProfile = useRiskProfile();
    const strategy = useStrategy({ portfolioId: portfolio?.Id });
    const { strategies } = useModelList({ productId });
    const { setSelectedProductType } = useCommonAllocationDataProvider();

    const { data: contactGroupDetails } = useGetCustomService({
        service: 'customClientService',
        api: 'getContactGroupDetails',
        payload: { groupId: portfolio?.ContactGroupId },
        enabled: !!portfolio?.ContactGroupId,
        staleTime: 20 * 60 * 1000, // 20 minutes
        gcTime: 25 * 60 * 1000, // 25 minutes
    });

    const portfolioOwners = useMemo(() => {
        const owners = contactGroupDetails?.data?.owners ? Object.keys(contactGroupDetails?.data?.owners)?.map(key => ({
            id: parseInt(key, 10),
            name: contactGroupDetails?.data?.owners?.[key],
        })) : [];

        return owners;
    }, [contactGroupDetails?.data?.owners]);

    const contactGroupAddress = useMemo(() => contactGroupDetails?.data?.contactGroupAddress || null, [contactGroupDetails?.data?.contactGroupAddress]);

    const mainOwner = useMemo(() => {
        const mainOwnerId = JSON.parse(sessionStorage.getItem(INVESTOR_PROFILE_CONTACT_ID) as string);
        const minOwnerId = mainOwnerId ?? Math.min(...portfolioOwners.map(i => i.id));

        return portfolioOwners.find(f => f.id === minOwnerId);
    }, [portfolioOwners]);

    const {
        data: mainOwnerData,
        error: mainOwnerError,
        isLoading: isMainOwnerLoading,
    } = useGetCustomService({
        service: 'clientService',
        api: 'getContact',
        payload: mainOwner?.id,
        enabled: !!mainOwner?.id,
        staleTime: 1000 * 60 * 60,
        gcTime: 1000 * 60 * 60 * 2,
    });

    const mainOwnerDetails = useMemo(() => {
        if (!mainOwnerData?.data || isEmpty(mainOwnerData?.data)) {
            return null;
        }

        return getModel(mainOwnerData?.data, getFormattedDate);
    }, [mainOwnerData, getFormattedDate]);

    const jointAccountClientId = useMemo(() => portfolioOwners.find(f => f.id !== mainOwnerDetails?.clientId)?.id, [mainOwnerDetails?.clientId, portfolioOwners]);

    const {
        data: jointOwnerData,
        error: jointOwnerError,
        isLoading: isJointOwnerLoading,
    } = useGetCustomService({
        service: 'clientService',
        api: 'getContact',
        payload: jointAccountClientId,
        enabled: !!jointAccountClientId,
        staleTime: 1000 * 60 * 60,
        gcTime: 1000 * 60 * 60 * 2,
    });

    const isThreeBProduct = useMemo(() => isAnyOfThreeBProductTypes(portfolio?.Product?.ExternalId), [portfolio?.Product?.ExternalId]);
    const isJointAccount = useMemo(() => portfolioOwners.length === 2, [portfolioOwners.length]);

    const jointAccountDetails = useMemo(() => {
        if (!jointOwnerData?.data || isEmpty(jointOwnerData?.data)) {
            return null;
        }

        return getModel(jointOwnerData?.data, getFormattedDate);
    }, [jointOwnerData, getFormattedDate]);

    useEffect(() => {
        setSelectedProductType(isThreeBProduct ? PRODUCTS.bbb : null);
    }, [isThreeBProduct, setSelectedProductType]);

    const state: any = useMemo(() => ({
        clientId,
        portfolioId,
        personalDetails,
        advisoryDocument,
        riskProfile,
        strategy,
        strategies,
        isThreeBProduct,
        portfolioOwners,
        isJointAccount,
        contactGroupId: portfolio?.ContactGroupId,
        productName: portfolio?.Product?.Name,
        productDescription: portfolio?.Product?.Description,
        mainOwnerDetails,
        contactGroupAddress,
        jointAccountDetails,
        productId,
        newProductExternalId,
    }), [
        clientId,
        portfolioId,
        personalDetails,
        advisoryDocument,
        riskProfile,
        strategy,
        strategies,
        isThreeBProduct,
        portfolioOwners,
        isJointAccount,
        portfolio?.ContactGroupId,
        portfolio?.Product?.Name,
        portfolio?.Product?.Description,
        mainOwnerDetails,
        contactGroupAddress,
        jointAccountDetails,
        productId,
        newProductExternalId,
    ]);

    return (
        <ChangeStrategyContext.Provider
            value={state}
        >
            <Preloader isLoading={isLoading || isMainOwnerLoading || isJointOwnerLoading} error={mainOwnerError || jointOwnerError}>
                {children}
            </Preloader>
        </ChangeStrategyContext.Provider>
    );
};

export default ChangeStrategyProvider;
